/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** @sfdc-extension-file SFDC_EXT_BNPL */
/* oxlint-disable react-refresh/only-export-components -- provider and hook are co-located by design */
import { createContext, useContext, type PropsWithChildren, type ReactElement } from 'react';
import type { BuyNowPayLaterMessageData, BuyNowPayLaterLearnMoreData } from '@/extensions/bnpl/lib/api/bnpl.server';

export interface BnplContextValue {
    messagePromise: Promise<BuyNowPayLaterMessageData>;
    learnMorePromise: Promise<BuyNowPayLaterLearnMoreData>;
}

const BnplContext = createContext<BnplContextValue | null>(null);

export function useBnpl(): BnplContextValue | null {
    return useContext(BnplContext);
}

export type BnplProviderProps = PropsWithChildren<{
    messagePromise: Promise<BuyNowPayLaterMessageData>;
    learnMorePromise: Promise<BuyNowPayLaterLearnMoreData>;
}>;

export function BnplProvider({ messagePromise, learnMorePromise, children }: BnplProviderProps): ReactElement {
    return <BnplContext.Provider value={{ messagePromise, learnMorePromise }}>{children}</BnplContext.Provider>;
}
